import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../utils/supabaseClient'; // Adjust path
import { useUser } from '../../../context/UserContext'; // Adjust path to your UserContext
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { ChevronLeftIcon, CheckCircleIcon, PlusCircleIcon, Loader2 } from 'lucide-react';

// --- NEW CUSTOM HOOK ---
// This hook manages the logic for checking and toggling the pay table status for the current company.
const useCompanyPayTableStatus = (payTableId) => {
  const { currentCompany } = useUser(); // Get company info from your context
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recordId, setRecordId] = useState(null);

  const checkStatus = useCallback(async () => {
    if (!payTableId || !currentCompany?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('company_pay_tables')
        .select('id')
        .eq('company_id', currentCompany.id)
        .eq('pay_table_id', payTableId)
        .maybeSingle();

      if (error) throw error;
      setIsEnabled(!!data);
      setRecordId(data?.id || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [payTableId, currentCompany?.id]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const toggleStatus = async () => {
    if (!payTableId || !currentCompany?.id) {
        setError("User or company not identified.");
        return;
    }
    setLoading(true);
    setError(null);
    try {
      if (isEnabled) {
        if (!recordId) throw new Error("Cannot disable: record ID not found.");
        const { error: deleteError } = await supabase.from('company_pay_tables').delete().match({ id: recordId });
        if (deleteError) throw deleteError;
        setIsEnabled(false);
        setRecordId(null);
      } else {
        const { data, error: insertError } = await supabase
          .from('company_pay_tables')
          .insert({
            company_id: currentCompany.id,
            pay_table_id: payTableId,
            // enabled_by: supabase.auth.user()?.id // Optional: track who enabled it
          })
          .select('id')
          .single();
        if (insertError) throw insertError;
        setIsEnabled(true);
        setRecordId(data.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { isEnabled, loading, error, toggleStatus };
};


// --- NEW DEDICATED BUTTON COMPONENT ---
const EnablePayTableButton = ({ payTableId }) => {
  const { isEnabled, loading, error, toggleStatus } = useCompanyPayTableStatus(payTableId);

  const buttonText = isEnabled ? 'Enabled for Company' : 'Enable for Company';
  const Icon = isEnabled ? CheckCircleIcon : PlusCircleIcon;

  const baseClasses = "inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors";
  const enabledClasses = "bg-green-600 text-white border-transparent hover:bg-green-700 focus:ring-green-500";
  const disabledClasses = "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-indigo-500";
  const loadingClasses = "bg-gray-100 text-gray-400 cursor-not-allowed";

  let buttonClasses = `${baseClasses} `;
  if (loading) buttonClasses += loadingClasses;
  else if (isEnabled) buttonClasses += enabledClasses;
  else buttonClasses += disabledClasses;
  
  return (
    <div className="flex flex-col items-end">
      <button
        type="button"
        className={buttonClasses}
        onClick={toggleStatus}
        disabled={loading}
      >
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Icon className="mr-2 h-5 w-5" />
        )}
        {loading ? 'Updating...' : buttonText}
      </button>
      {error && <p className="text-xs text-red-600 mt-1">Error: {error}</p>}
    </div>
  );
};


// --- Helper & UI Components (self-contained for simplicity) ---

const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen bg-gray-50">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
  </div>
);

const ErrorMessage = ({ message }) => (
  <div className="p-8 bg-gray-50 min-h-screen">
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-800">
      <h3 className="font-bold text-lg">Error Loading Pay Table</h3>
      <p>{message || 'The requested data could not be found. Please try again later.'}</p>
    </div>
  </div>
);

const Card = ({ title, children, className = '' }) => (
  <section className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
    <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 px-6 py-4">
      {title}
    </h2>
    <div className="p-6 text-gray-700 space-y-4">
      {children}
    </div>
  </section>
);

const PayFactorTable = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <p className="text-gray-500 italic">No pay factor data is available.</p>;
  }

  // Helper to render the appropriate pay factor and notes
  const renderPayFactor = (row) => {
    if (row.pfs) {
      return <InlineMath math={String(row.pfs)} />;
    }

    const typeA = row.pfs_typeA !== null && (
      <div>
        <p>
          <span className="font-semibold text-sm">Type A:</span> <InlineMath math={String(row.pfs_typeA)} />
        </p>
        {row.note_typeA && <p className="text-xs text-gray-500 italic leading-tight">{row.note_typeA}</p>}
      </div>
    );

    const typeB = row.pfs_typeB !== null && (
      <div>
        <p>
          <span className="font-semibold text-sm">Type B:</span> <InlineMath math={String(row.pfs_typeB)} />
        </p>
        {row.note_typeB && <p className="text-xs text-gray-500 italic leading-tight">{row.note_typeB}</p>}
      </div>
    );

    if (typeA || typeB) {
      return <div className="space-y-2">{[typeA, typeB]}</div>;
    }
    
    return 'N/A';
  };

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm text-left text-gray-800">
        <thead className="text-xs text-gray-700 uppercase bg-gray-100">
          <tr>
            <th scope="col" className="px-6 py-3">MRI (in./mi.)</th>
            <th scope="col" className="px-6 py-3">Pay Factor, <InlineMath math="PF_s" /></th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index} className="bg-white border-b last:border-b-0 hover:bg-gray-50">
              <td className="px-6 py-4 font-mono">{row.mri}</td>
              <td className="px-6 py-4">{renderPayFactor(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PayAdjustmentFormula = () => {
  const formula = 'q_s = (PF_s - 1.00) \\times \\frac{A \\times S}{T} \\times \\frac{U}{n}';
  return (
    <>
      <p>The quality assurance adjustment per section (<InlineMath math="q_s" />) is calculated using the following formula:</p>
      <div className="bg-gray-50 p-4 rounded-md text-center text-lg my-4">
        <BlockMath math={formula} />
      </div>
      <div>
        <h4 className="font-semibold text-gray-800 mb-2">Where:</h4>
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li><span className="font-semibold w-8 inline-block"><InlineMath math="A" /></span>: Area of the section (sq yd)</li>
          <li><span className="font-semibold w-8 inline-block"><InlineMath math="S" /></span>: Planned spread rate (lb/sq yd)</li>
          <li><span className="font-semibold w-8 inline-block"><InlineMath math="PF_s" /></span>: Pay factor from the table for the section</li>
          <li><span className="font-semibold w-8 inline-block"><InlineMath math="T" /></span>: Conversion factor (2,000 lb/ton)</li>
          <li><span className="font-semibold w-8 inline-block"><InlineMath math="U" /></span>: Unit price of the material ($/ton)</li>
          <li><span className="font-semibold w-8 inline-block"><InlineMath math="n" /></span>: Number of layers in the section</li>
        </ul>
      </div>
      <p className="text-sm mt-4">The total project adjustment, <InlineMath math="Q_s" />, is the sum of all individual section adjustments (<InlineMath math="q_s" />).</p>
    </>
  );
};

// --- Custom Hook for Data Fetching ---

const usePayTable = (tableName) => {
  const [payTable, setPayTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPayTable = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('pay_tables')
          .select('*')
          .eq('name', tableName)
          .single();

        if (error) throw error;
        setPayTable(data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (tableName) {
      fetchPayTable();
    }
  }, [tableName]);

  return { payTable, loading, error };
};

// --- Main Page Component ---

export default function IndotPayTable() {
  const { payTable, loading, error } = usePayTable('INDOT');
  const { loading: userLoading } = useUser(); // To prevent rendering button before company is loaded

  if (loading || userLoading) {
    return <LoadingSpinner />;
  }

  if (error || !payTable) {
    return <ErrorMessage message={error?.message} />;
  }

  return (
    <div className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">

        <div className="mb-6">
          <Link to="/settings?tab=paytables" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900">
            <ChevronLeftIcon className="w-5 h-5 mr-1" />
            Back to All Pay Tables
          </Link>
        </div>

        {/* Header */}
        <header className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">INDOT Pay Table</h1>
            <p className="text-md text-gray-500">Smoothness Specification for Asphalt Pavement</p>
          </div>
          
          {/* --- NEW BUTTON INTEGRATION --- */}
          <div>
            <EnablePayTableButton payTableId={payTable.id} />
          </div>
        </header>

        {/* Main Content Grid */}
        <main className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            <Card title="How INDOT Pay Factors Work">
              <p>The Indiana Department of Transportation (INDOT) uses a pay factor system applied per 0.1-mile segment based on Mean Roughness Index (MRI) values. Pay factors (<InlineMath math="PF_s" />) are determined from the MRI of each section. The system provides incentives for very smooth pavements and applies disincentives for rougher sections.</p>
              <p>For high MRI values (over 90 in/mi), the pay factors and mandatory corrective actions differ based on the pavement type (Type A vs. Type B), as shown in the table below.</p>
            </Card>

            <Card title="Pay Factor Table">
              <PayFactorTable data={payTable.json_table} />
              <div className="mt-4 p-4 bg-gray-50 rounded-md border text-sm">
                <h4 className="font-semibold text-gray-800 mb-1">Posted Speed</h4>
                <p>This table is applicable to projects where the posted speed is greater than 45 mph.</p>
              </div>
            </Card>

            <Card title="Pay Adjustment Formula">
              <PayAdjustmentFormula />
            </Card>
          </div>

          {/* Right Column (Sidebar) */}
          <div className="lg:col-span-1 space-y-8">
            <Card title="Overview">
              <div className="space-y-3 text-sm">
                <p><span className="font-semibold text-gray-900">Agency:</span> Indiana Department of Transportation</p>
                <p><span className="font-semibold text-gray-900">Docs:</span> <a href="https://www.in.gov/dot/div/contracts/standards/book/sep23/400-2024.pdf" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Standard Specifications</a></p>
                <p><span className="font-semibold text-gray-900">Website:</span> <a href="https://www.in.gov/indot/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">indot.in.gov</a></p>
              </div>
            </Card>
            
            <Card title="Database Integration">
                <p className="text-sm">This pay table integrates MRI-based pay factors directly from your database. Use these values to automate payment adjustments in your projects.</p>
                
            </Card>

            <Card title="Recent Updates">
              <p className="text-sm">As of August 2025, no major changes to smoothness pay factors from 2024 specifications.</p>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
